import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Text, Button, EmptyState, QueryChips } from '@teals/ui-web';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  useGetAnalyticsBreakdownQuery,
  useGetAnalyticsSessionQuery,
  useGetAnalyticsSessionsQuery,
  useGetAnalyticsSummaryQuery,
  useGetAnalyticsTimeseriesQuery,
} from '../../store/api/analyticsApi';
import AnalyticsAskBar from './AnalyticsAskBar';
import AnalyticsReloadButton from './AnalyticsReloadButton';
import {
  formatPct,
  formatSessionId,
  formatTokens,
  isCostQuery,
  rangeIso,
} from './analyticsFormat';
import {
  AskSection,
  BreakdownTable,
  HeaderActions,
  BudgetActions,
  BudgetHeader,
  BudgetRow,
  ChartLegend,
  ContentGrid,
  ControlGroup,
  ControlLabel,
  ControlsBar,
  DimensionRow,
  EmptyShell,
  InsightActions,
  InsightHero,
  InsightLabel,
  InsightText,
  KpiCard,
  KpiChange,
  KpiLabel,
  KpiStrip,
  KpiValue,
  Overlay,
  PageHeader,
  PageWrapper,
  PanelCard,
  PanelEmpty,
  Progress,
  RangeText,
  SecondaryDisclosure,
  SectionSub,
  SectionTitle,
  SpotBody,
  SpotHeader,
  SpotMeta,
  SpotRow,
  SpotSlideOver,
  SpotTokens,
  SpotTop,
  Subtitle,
} from './Analytics.styles';

const RANGE_PRESETS = [
  { id: '24h', label: '24h' },
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
  { id: '90d', label: '90d' },
];

const RANGE_DAYS: Record<string, number> = {
  '24h': 1,
  '7d': 7,
  '30d': 30,
  '90d': 90,
};

const GRAINS = [
  { id: 'hour', label: 'Hour' },
  { id: 'day', label: 'Day' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
  { id: 'year', label: 'Year' },
];

const DIMENSIONS = [
  { id: 'application', label: 'Application' },
  { id: 'agent', label: 'Agent' },
  { id: 'model', label: 'Model' },
];

const SUGGESTED_QUERIES = [
  'Top agents by tokens',
  'Models by volume',
  'Heaviest sessions this week',
  'Trend this month',
];

function formatRangeLabel(from: string, to: string): string {
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };
  return `${new Date(from).toLocaleDateString(undefined, opts)} – ${new Date(
    to,
  ).toLocaleDateString(undefined, opts)}`;
}

const Analytics: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rangeId, setRangeId] = useState('30d');
  const [grain, setGrain] = useState('day');
  const [ask, setAsk] = useState('');
  const [spotSessionId, setSpotSessionId] = useState<string | null>(null);

  const dimension =
    searchParams.get('dimension') ||
    (searchParams.get('agent')
      ? 'agent'
      : searchParams.get('model')
        ? 'model'
        : 'application');
  const applicationId =
    searchParams.get('application') ||
    searchParams.get('application_id') ||
    undefined;
  const agentName =
    searchParams.get('agent') || searchParams.get('agent_name') || undefined;
  const model = searchParams.get('model') || undefined;

  const days = RANGE_DAYS[rangeId] ?? 30;
  const { from, to } = useMemo(() => rangeIso(days), [days]);

  const queryArgs = {
    from,
    to,
    grain,
    applicationId,
    agentName,
    model,
  };

  const {
    data: summary,
    isLoading,
    isError,
    error,
    refetch: refetchSummary,
  } = useGetAnalyticsSummaryQuery(queryArgs);
  const { data: timeseries, refetch: refetchTimeseries } =
    useGetAnalyticsTimeseriesQuery({
      ...queryArgs,
      metric: 'tokens_total',
    });
  const { data: breakdown, refetch: refetchBreakdown } =
    useGetAnalyticsBreakdownQuery({
      ...queryArgs,
      dimension,
    });
  const { data: topSessions, refetch: refetchSessions } =
    useGetAnalyticsSessionsQuery({
      ...queryArgs,
      limit: 10,
    });
  const { data: spotDetail } = useGetAnalyticsSessionQuery(spotSessionId || '', {
    skip: !spotSessionId,
  });

  const reloadAnalytics = () => {
    void refetchSummary();
    void refetchTimeseries();
    void refetchBreakdown();
    void refetchSessions();
  };
  const isEmpty =
    !isLoading &&
    !isError &&
    (summary?.tokensTotal ?? 0) === 0 &&
    (summary?.sessions ?? 0) === 0;

  const chartData = (timeseries?.points || []).map((p) => ({
    bucket: p.bucket,
    tokens: p.value,
  }));

  const hints = summary?.insightHints;
  const tokensPct = summary?.periodComparison?.tokensTotalPct ?? hints?.tokensTotalPct;
  const sessionsPct = summary?.periodComparison?.sessionsPct;

  const insightBody = (() => {
    if (isLoading) {
      return <>Loading token health for this organisation…</>;
    }
    if (isEmpty) {
      return (
        <>
          No token usage in this range yet. Run an agent in{' '}
          <strong>Playground</strong> or wait for production traffic — then token
          health and drivers will appear here.
        </>
      );
    }
    const pct = hints?.tokensTotalPct ?? tokensPct;
    const driver = hints?.topDriverAgent;
    const share = hints?.topDriverSharePct;
    if (pct == null && !driver) {
      return <>Review token health for this organisation in the selected range.</>;
    }
    const trend =
      pct == null ? (
        <>Token volume is ready to review</>
      ) : pct >= 0 ? (
        <>
          Token volume is <strong>up {pct}%</strong> vs prior period
        </>
      ) : (
        <>
          Token volume is <strong>down {Math.abs(pct)}%</strong> vs prior period
        </>
      );
    if (driver && share != null) {
      return (
        <>
          {trend}. <strong>{driver}</strong> drove <strong>{share}%</strong> of
          tokens.
        </>
      );
    }
    return <>{trend}.</>;
  })();

  const setDimension = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('dimension', id);
    setSearchParams(next);
  };

  const applyAsk = (label: string) => {
    if (isCostQuery(label)) {
      navigate('/payments');
      return;
    }
    const lower = label.toLowerCase();
    if (lower.includes('agent')) setDimension('agent');
    else if (lower.includes('model')) setDimension('model');
    else if (lower.includes('month') || lower.includes('trend')) {
      setRangeId('30d');
      setGrain('day');
    } else if (lower.includes('week') || lower.includes('heaviest')) {
      setRangeId('7d');
      setGrain('day');
    }
  };

  const driverOpenLink = (key: string) => {
    if (dimension === 'application') {
      if (key === 'unassigned') return null;
      return `/applications/${encodeURIComponent(key)}`;
    }
    if (dimension === 'agent') {
      return `/analytics?agent=${encodeURIComponent(key)}&dimension=agent`;
    }
    return `/analytics?model=${encodeURIComponent(key)}&dimension=model`;
  };

  const topBreakdown = breakdown?.rows?.[0];
  const topDriverLabel =
    topBreakdown?.label ||
    (topBreakdown?.key && topBreakdown.key !== 'unassigned'
      ? topBreakdown.key
      : null) ||
    (topBreakdown?.key === 'unassigned' ? 'Playground' : null);

  if (isError) {
    const status = (error as { status?: number })?.status;
    if (status === 403) {
      return (
        <PageWrapper>
          <Text variant="h2">Analytics Center</Text>
          <EmptyShell>
            <EmptyState
              title="No permission"
              description="You do not have permission to view analytics for this organisation."
            />
          </EmptyShell>
        </PageWrapper>
      );
    }
    return (
      <PageWrapper>
        <Text variant="h2">Analytics Center</Text>
        <EmptyShell>
          <EmptyState
            title="Could not load analytics"
            description="Check your organisation context and try again."
            action={
              <Button variant="primary" size="sm" onClick={() => refetchSummary()}>
                Retry
              </Button>
            }
          />
        </EmptyShell>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <PageHeader>
        <div>
          <Text variant="h2">Analytics Center</Text>
          <Subtitle>
            Token and usage health · costs live on{' '}
            <Link to="/payments">Payments</Link>
          </Subtitle>
        </div>
        <HeaderActions>
          <AnalyticsReloadButton onReload={reloadAnalytics} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/payments')}
          >
            View charges on Payments
          </Button>
        </HeaderActions>
      </PageHeader>

      <InsightHero aria-label="Proactive insight">
        <InsightLabel>Insight · last {rangeId}</InsightLabel>
        <InsightText>{insightBody}</InsightText>
        <InsightActions>
          {!isEmpty && hints?.topDriverAgent && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setDimension('agent')}
            >
              Inspect {hints.topDriverAgent}
            </Button>
          )}
          {isEmpty && (
            <Button variant="primary" size="sm" onClick={() => navigate('/agents')}>
              Open Agents
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/payments')}
          >
            {isEmpty ? 'View Payments' : 'View charges on Payments'}
          </Button>
        </InsightActions>
      </InsightHero>

      <AskSection>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            applyAsk(ask);
          }}
        >
          <AnalyticsAskBar
            value={ask}
            onChange={setAsk}
            onSubmit={() => applyAsk(ask)}
            placeholder="Ask about tokens, sessions, or usage…"
          />
        </form>
        <div style={{ marginTop: '0.75rem' }}>
          <QueryChips
            chips={SUGGESTED_QUERIES.map((label) => ({ label }))}
            onSelect={applyAsk}
          />
        </div>
      </AskSection>

      <ControlsBar>
        <ControlGroup>
          <ControlLabel>Range</ControlLabel>
          {RANGE_PRESETS.map((r) => (
            <Button
              key={r.id}
              size="sm"
              variant={rangeId === r.id ? 'primary' : 'outline'}
              onClick={() => setRangeId(r.id)}
            >
              {r.label}
            </Button>
          ))}
          <RangeText>{formatRangeLabel(from, to)}</RangeText>
        </ControlGroup>
        <ControlGroup>
          <ControlLabel>Grain</ControlLabel>
          {GRAINS.map((g) => (
            <Button
              key={g.id}
              size="sm"
              variant={grain === g.id ? 'primary' : 'outline'}
              onClick={() => setGrain(g.id)}
            >
              {g.label}
            </Button>
          ))}
        </ControlGroup>
      </ControlsBar>

      {isLoading ? (
        <PanelCard style={{ marginBottom: '1.25rem' }}>
          <SectionSub>Loading usage…</SectionSub>
        </PanelCard>
      ) : null}

      {!isLoading && (
        <>
          <KpiStrip>
            <KpiCard>
              <KpiLabel>Tokens in</KpiLabel>
              <KpiValue>{formatTokens(summary?.tokensIn)}</KpiValue>
              {formatPct(tokensPct) && (
                <KpiChange $tone={(tokensPct || 0) >= 0 ? 'up' : 'down'}>
                  {(tokensPct || 0) >= 0 ? '↑' : '↓'} {formatPct(tokensPct)}
                </KpiChange>
              )}
            </KpiCard>
            <KpiCard>
              <KpiLabel>Tokens out</KpiLabel>
              <KpiValue>{formatTokens(summary?.tokensOut)}</KpiValue>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Sessions</KpiLabel>
              <KpiValue>{formatTokens(summary?.sessions)}</KpiValue>
              {formatPct(sessionsPct) && (
                <KpiChange $tone={(sessionsPct || 0) >= 0 ? 'up' : 'down'}>
                  {(sessionsPct || 0) >= 0 ? '↑' : '↓'} {formatPct(sessionsPct)}
                </KpiChange>
              )}
            </KpiCard>
            <KpiCard>
              <KpiLabel>Budget utilisation</KpiLabel>
              <KpiValue>
                {summary?.budgetUtilisation?.organisation?.utilisationPct != null
                  ? `${summary.budgetUtilisation.organisation.utilisationPct}%`
                  : '—'}
              </KpiValue>
              <KpiChange $tone="neutral">
                {summary?.budgetUtilisation?.organisation?.tokenCap != null
                  ? 'Organisation cap'
                  : 'No org cap set'}
              </KpiChange>
            </KpiCard>
          </KpiStrip>

          <SecondaryDisclosure>
            <summary>Secondary metrics</summary>
            <p>
              API calls {formatTokens(summary?.exchangeCount)} · Reasoning tokens{' '}
              {formatTokens(summary?.reasoningTokens)} · Active agents{' '}
              {summary?.activeAgents ?? 0} · Active applications{' '}
              {summary?.activeApplications ?? 0}
            </p>
            <p>
              Dollar charges and negotiated rates are on{' '}
              <Link to="/payments">Payments</Link> — not shown here.
            </p>
          </SecondaryDisclosure>

          <DimensionRow role="tablist" aria-label="Analytics dimension">
            <ControlLabel>Dimension</ControlLabel>
            {DIMENSIONS.map((d) => (
              <Button
                key={d.id}
                size="sm"
                variant={dimension === d.id ? 'primary' : 'outline'}
                onClick={() => setDimension(d.id)}
              >
                {d.label}
              </Button>
            ))}
          </DimensionRow>

          {isEmpty ? (
            <>
              <EmptyShell>
                <EmptyState
                  title="No usage yet"
                  description="Analytics is scoped to the active organisation. Token trends, top drivers, and session spot analysis will show once harness traffic is recorded."
                  action={
                    <>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => navigate('/agents')}
                      >
                        Try an agent
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setRangeId('90d')}
                      >
                        Widen to 90d
                      </Button>
                    </>
                  }
                />
              </EmptyShell>
              <ContentGrid>
                <PanelCard>
                  <SectionTitle>Tokens over time</SectionTitle>
                  <SectionSub>
                    {grain} grain · click a bar for spot analysis
                  </SectionSub>
                  <PanelEmpty>
                    No token volume in this range yet.
                  </PanelEmpty>
                </PanelCard>
                <PanelCard>
                  <SectionTitle>Top drivers</SectionTitle>
                  <SectionSub>By token volume · within active org</SectionSub>
                  <PanelEmpty>Drivers appear after usage is recorded.</PanelEmpty>
                </PanelCard>
              </ContentGrid>
            </>
          ) : (
            <>
              <ContentGrid>
                <PanelCard>
                  <SectionTitle>Tokens over time</SectionTitle>
                  <SectionSub>
                    {grain} grain · click a bar for spot analysis
                  </SectionSub>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={chartData}
                      onClick={() => {
                        const sess = topSessions?.sessions?.[0];
                        if (sess) setSpotSessionId(sess.sessionId);
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis dataKey="bucket" hide={chartData.length > 14} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="tokens" cursor="pointer">
                        {chartData.map((entry, i) => {
                          const max = Math.max(
                            0,
                            ...chartData.map((d) => d.tokens),
                          );
                          return (
                            <Cell
                              key={`${entry.bucket}-${i}`}
                              fill={
                                entry.tokens === max && max > 0
                                  ? 'var(--warning)'
                                  : 'var(--accent)'
                              }
                            />
                          );
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <ChartLegend>
                    <span>
                      {topDriverLabel
                        ? `Top ${dimension} · ${topDriverLabel}`
                        : hints?.topDriverAgent
                          ? `Top agent · ${hints.topDriverAgent}`
                          : 'Click a bar for spot analysis'}
                    </span>
                    <span>
                      {formatPct(tokensPct)
                        ? `vs prior period ${formatPct(tokensPct)}`
                        : null}
                    </span>
                  </ChartLegend>
                </PanelCard>

                <PanelCard>
                  <SectionTitle>Top drivers</SectionTitle>
                  <SectionSub>
                    By token volume · within active org
                    {dimension === 'application' &&
                    (breakdown?.rows || []).some((r) => r.key === 'unassigned')
                      ? ' · “Playground” is portal/direct traffic with no linked application'
                      : ''}
                  </SectionSub>
                  <BreakdownTable>
                    <thead>
                      <tr>
                        <th>Driver</th>
                        <th>Tokens</th>
                        <th>Exchanges</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {(breakdown?.rows || []).map((row) => {
                        const href = driverOpenLink(row.key);
                        const display =
                          row.label ||
                          (row.key === 'unassigned' ? 'Playground' : row.key);
                        return (
                          <tr key={row.key}>
                            <td>{display}</td>
                            <td className="mono">
                              {formatTokens(row.tokensTotal)}
                            </td>
                            <td className="mono">
                              {formatTokens(row.exchangeCount)}
                            </td>
                            <td>
                              {href ? <Link to={href}>Open</Link> : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </BreakdownTable>
                </PanelCard>
              </ContentGrid>

              <PanelCard style={{ marginBottom: '1.25rem' }}>
                <SectionTitle>Top sessions by tokens</SectionTitle>
                <SectionSub>Click a row for spot analysis</SectionSub>
                <BreakdownTable>
                  <thead>
                    <tr>
                      <th>Session</th>
                      <th>Agent</th>
                      <th>Tokens</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(topSessions?.sessions || []).map((s) => (
                      <tr
                        key={s.sessionId}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSpotSessionId(s.sessionId)}
                      >
                        <td className="mono" title={s.sessionId}>
                          {formatSessionId(s.sessionId)}
                        </td>
                        <td>{s.agentName || '—'}</td>
                        <td className="mono">{formatTokens(s.tokensTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </BreakdownTable>
              </PanelCard>
            </>
          )}

          <PanelCard>
            <SectionTitle>Budget utilisation</SectionTitle>
            <SectionSub>
              Observe here · edit <strong>org</strong> budget on{' '}
              <Link to="/payments">Payments</Link> · edit <strong>app</strong>{' '}
              budgets on Application Configuration · charges on Payments
            </SectionSub>
            <BudgetRow>
              <BudgetHeader>
                <span>Organisation-wide cap</span>
                <strong>
                  {summary?.budgetUtilisation?.organisation?.utilisationPct != null
                    ? `${summary.budgetUtilisation.organisation.utilisationPct}% used`
                    : 'No cap set'}
                </strong>
              </BudgetHeader>
              <Progress
                $pct={
                  summary?.budgetUtilisation?.organisation?.utilisationPct || 0
                }
              />
              <BudgetActions>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/payments')}
                >
                  Adjust org budget
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/payments')}
                >
                  View charges
                </Button>
              </BudgetActions>
            </BudgetRow>
            {(summary?.budgetUtilisation?.applications || []).map((app) => (
              <BudgetRow key={app.applicationId || app.name}>
                <BudgetHeader>
                  <span>{app.name || app.applicationId} — Application cap</span>
                  <strong>
                    {app.utilisationPct != null
                      ? `${app.utilisationPct}% used`
                      : 'No cap'}
                  </strong>
                </BudgetHeader>
                <Progress $pct={app.utilisationPct || 0} />
                <BudgetActions>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      navigate(`/applications/${app.applicationId}`)
                    }
                  >
                    Adjust app budget
                  </Button>
                </BudgetActions>
              </BudgetRow>
            ))}
            {!summary?.budgetUtilisation?.applications?.length && (
              <SectionSub style={{ marginTop: '0.5rem' }}>
                Application caps appear once apps have budget_config.token_cap
                set.
              </SectionSub>
            )}
          </PanelCard>
        </>
      )}

      {spotSessionId && (
        <>
          <Overlay onClick={() => setSpotSessionId(null)} />
          <SpotSlideOver aria-label="Spot analysis">
            <SpotHeader>
              <div>
                <strong>Session spot analysis</strong>
                <SpotMeta>{spotSessionId}</SpotMeta>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSpotSessionId(null)}
              >
                Close
              </Button>
            </SpotHeader>
            <SpotBody>
              <SpotRow>
                <SpotTop>
                  <span>Token mix</span>
                  <span>{formatTokens(spotDetail?.tokensTotal)} total</span>
                </SpotTop>
                <SpotTokens>
                  In {formatTokens(spotDetail?.tokensIn)} · Out{' '}
                  {formatTokens(spotDetail?.tokensOut)} · Reasoning{' '}
                  {formatTokens(spotDetail?.reasoningTokens)}
                </SpotTokens>
                <SpotMeta>
                  Models: {(spotDetail?.models || []).join(', ') || '—'}
                </SpotMeta>
              </SpotRow>
              {(spotDetail?.exchanges || []).slice(0, 5).map((ex) => (
                <SpotRow key={ex.exchangeId}>
                  <SpotTop>
                    <span>
                      {ex.exchangeId.slice(0, 10)}… · {ex.agentName}
                    </span>
                    <span>{formatTokens(ex.totalTokens)}</span>
                  </SpotTop>
                  <SpotMeta>
                    {ex.applicationId || '—'} · {ex.model}
                    {ex.createdAt ? ` · ${ex.createdAt}` : ''}
                  </SpotMeta>
                  <SpotTokens>
                    In {formatTokens(ex.inputTokens)} · Out{' '}
                    {formatTokens(ex.outputTokens)}
                    {ex.reasoningTokens
                      ? ` · Reasoning ${formatTokens(ex.reasoningTokens)}`
                      : ''}
                  </SpotTokens>
                </SpotRow>
              ))}
              <SectionSub style={{ marginTop: '1.25rem' }}>
                Analytics shows token usage only. Charges at your negotiated rate
                are on Payments.
              </SectionSub>
              <BudgetActions>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate('/agents')}
                >
                  Open Agents
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/payments')}
                >
                  View charges
                </Button>
              </BudgetActions>
            </SpotBody>
          </SpotSlideOver>
        </>
      )}
    </PageWrapper>
  );
};

export default Analytics;
